import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Anchor,
  Button,
  Container,
  Group,
  Paper,
  Stack,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { IconBuilding, IconPlus, IconSearch } from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import { useCompaniesList, useCreateCompany } from '../../../queries/companies';
import { ApiError } from '../../../lib/api-error';

export function CompaniesListPage() {
  const [q, setQ] = useState('');
  const filters = useMemo(() => ({ q: q || undefined }), [q]);
  const { data, isLoading, isError, error } = useCompaniesList(filters);
  const createM = useCreateCompany();
  const navigate = useNavigate();

  function openCreate() {
    let name = '';
    let description = '';
    modals.openConfirmModal({
      title: 'New company',
      children: (
        <Stack gap="sm">
          <TextInput
            label="Name"
            placeholder="Acme Holdings"
            data-autofocus
            onChange={(e) => {
              name = e.currentTarget.value;
            }}
          />
          <Textarea
            label="Description"
            minRows={2}
            onChange={(e) => {
              description = e.currentTarget.value;
            }}
          />
        </Stack>
      ),
      labels: { confirm: 'Create company', cancel: 'Cancel' },
      onConfirm: () => {
        const trimmed = name.trim();
        if (!trimmed) {
          notifications.show({ title: 'Name is required', message: '', color: 'red' });
          return;
        }
        createM.mutate(
          { name: trimmed, description: description.trim() || null },
          {
            onSuccess: ({ company }) => {
              notifications.show({
                title: 'Company created',
                message: company.name,
                color: 'pine',
              });
              navigate(`/admin/companies/${company.id}`);
            },
            onError: (e: Error) =>
              notifications.show({
                title: 'Could not create company',
                message: e instanceof ApiError ? e.message : e.message,
                color: 'red',
              }),
          },
        );
      },
    });
  }

  return (
    <Container size="lg">
      <Stack gap="md">
        <Group justify="space-between" align="flex-end">
          <Stack gap={4}>
            <Title order={1}>Companies</Title>
            <Group gap={6}>
              <IconBuilding size={16} />
              <Anchor component={Link} to="/admin">
                ← Back to Admin
              </Anchor>
            </Group>
          </Stack>
          <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
            New company
          </Button>
        </Group>

        <Group>
          <TextInput
            placeholder="Search companies"
            leftSection={<IconSearch size={14} />}
            value={q}
            onChange={(e) => setQ(e.currentTarget.value)}
            style={{ flex: 1, maxWidth: 360 }}
          />
        </Group>

        <Paper withBorder radius="md">
          <DataTable
            records={data?.companies ?? []}
            fetching={isLoading}
            minHeight={150}
            noRecordsText={isError ? `Error: ${error?.message ?? 'unknown'}` : 'No companies yet'}
            highlightOnHover
            onRowClick={({ record }) => navigate(`/admin/companies/${record.id}`)}
            columns={[
              {
                accessor: 'name',
                title: 'Name',
                render: (c) => (
                  <Anchor component={Link} to={`/admin/companies/${c.id}`}>
                    {c.name}
                  </Anchor>
                ),
              },
              { accessor: 'slug', title: 'Slug', width: 200 },
              {
                accessor: 'created_at',
                title: 'Created',
                width: 150,
                render: (c) => dayjs(c.created_at).format('YYYY-MM-DD'),
              },
            ]}
          />
        </Paper>
      </Stack>
    </Container>
  );
}
